// Barres compactes : PV, mana, énergie (⚡ pastilles), satiété, flamme (🔥×3), or, niveau + XP ; avertissement « serment des 24 h » quand driftAt approche.
import type { CharacterView, ConditionId } from "../../shared/types";
import { HOUR, MINUTE } from "../../shared/types";
import "./StatusBar.css";

export interface StatusBarProps { c: CharacterView; now: number }

const CONDITION_LABELS: Record<ConditionId, string> = {
  empoisonne: "Empoisonné",
  saignement: "Saignement",
  etourdi: "Étourdi",
  beni: "Béni",
  affame: "Affamé",
  famine: "Famine",
  epuise: "Épuisé",
  invisible: "Invisible",
  protege: "Protégé",
  enflamme: "Enflammé",
  gele: "Gelé",
  maudit: "Maudit",
  inspire: "Inspiré",
  cicatrice_oubli: "Cicatrice d'oubli",
  repose: "Reposé",
};

const CONDITION_ICONS: Record<ConditionId, string> = {
  empoisonne: "☠️",
  saignement: "🩸",
  etourdi: "💫",
  beni: "✨",
  affame: "🍂",
  famine: "🥀",
  epuise: "😮‍💨",
  invisible: "👻",
  protege: "🛡️",
  enflamme: "🔥",
  gele: "❄️",
  maudit: "🌑",
  inspire: "🌟",
  cicatrice_oubli: "🩹",
  repose: "🌙",
};

// Heure et jour à Paris, via Intl (pas d'horloge système).
const PARIS_CLOCK = new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", hour: "numeric", minute: "2-digit", hourCycle: "h23" });
const PARIS_WEEKDAY = new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", weekday: "short" });
const PARIS_DAY = new Intl.DateTimeFormat("fr-CA", { timeZone: "Europe/Paris", year: "numeric", month: "2-digit", day: "2-digit" });

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function parisClock(at: number): { h: number; m: number } {
  const parts = PARIS_CLOCK.formatToParts(new Date(at));
  const h = Number(parts.find((p) => p.type === "hour")?.value ?? 0) % 24;
  const m = Number(parts.find((p) => p.type === "minute")?.value ?? 0);
  return { h, m };
}

/** « 18 h 40 », heure de Paris. */
function heureFr(at: number): string {
  const { h, m } = parisClock(at);
  return `${h} h ${pad2(m)}`;
}

/** « 07:00 », heure de Paris. */
function heureCourte(at: number): string {
  const { h, m } = parisClock(at);
  return `${pad2(h)}:${pad2(m)}`;
}

/** « 18 h 40 » le jour même, sinon « mar. 18 h 40 ». */
function quand(at: number, now: number): string {
  const sameDay = PARIS_DAY.format(new Date(at)) === PARIS_DAY.format(new Date(now));
  return sameDay ? heureFr(at) : `${PARIS_WEEKDAY.format(new Date(at))} ${heureFr(at)}`;
}

/** Durée restante : « 4 h 12 » ou « 35 min ». */
function restant(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / MINUTE));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h === 0 ? `${m} min` : `${h} h ${pad2(m)}`;
}

/** Rapport borné entre 0 et 1 (0 si le maximum est nul). */
function ratio(value: number, max: number): number {
  return max > 0 ? Math.min(1, Math.max(0, value / max)) : 0;
}

function percent(r: number): string {
  return `${(r * 100).toFixed(1)}%`;
}

export function StatusBar(props: StatusBarProps) {
  const { c, now } = props;

  const hpRatio = ratio(c.hp, c.hpMax);
  const manaRatio = ratio(c.mana, c.manaMax);
  const hungry = c.satiety <= 20 || c.conditions.some((k) => k.id === "affame" || k.id === "famine");
  const hungerRatio = ratio(c.satiety, 100);

  const xpSpan = c.xpForNext - c.xpForCurrent;
  const maxedXp = xpSpan <= 0;
  const xpRatio = maxedXp ? 1 : ratio(c.xp - c.xpForCurrent, xpSpan);

  // Serment des 24 h : avertir six heures avant l'échéance, puis une fois dépassée.
  const toDrift = c.driftAt - now;
  const driftWarning = c.flame > 0 && c.lifeState !== "mort" && toDrift < 6 * HOUR;

  const agonyUntil = c.lifeState === "agonie" ? (c.agonieUntil ?? null) : null;
  const sleepUntil = c.lifeState === "endormi" && c.sleepUntil !== null && c.sleepUntil > now ? c.sleepUntil : null;

  const travel = c.travel;
  const travelRatio = travel ? ratio(now - travel.departAt, travel.arriveAt - travel.departAt) : 0;

  return (
    <section class="status-bar" aria-label="État du personnage">
      {agonyUntil !== null && (
        <div class="status-bar-special status-bar-agonie" role="alert">
          <span class="status-bar-special-icon" aria-hidden="true">❤️‍🩹</span>
          <span>
            Agonie : il te reste <strong>{restant(agonyUntil - now)}</strong>. Un allié peut te soigner, ou tu peux t'accrocher à la vie.
          </span>
        </div>
      )}

      {travel && (
        <div class="status-bar-special status-bar-voyage">
          <div class="status-bar-special-ligne">
            <span class="status-bar-special-icon" aria-hidden="true">🧭</span>
            <span>
              En route vers <strong>{travel.toName}</strong>, arrivée à <strong>{quand(travel.arriveAt, now)}</strong>
            </span>
          </div>
          <div
            class="status-bar-voyage-piste"
            role="progressbar"
            aria-label="Progression du voyage"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(travelRatio * 100)}
          >
            <div class="status-bar-voyage-fill" style={{ width: percent(travelRatio) }} />
          </div>
        </div>
      )}

      {sleepUntil !== null && (
        <div class="status-bar-special status-bar-sommeil">
          <span class="status-bar-special-icon" aria-hidden="true">😴</span>
          <span>Endormi jusqu'à <strong>{heureCourte(sleepUntil)}</strong></span>
        </div>
      )}

      {driftWarning && (
        <div class="status-bar-avertissement" role="status">
          <span aria-hidden="true">🕯️</span>
          {toDrift > 0 ? (
            <span>
              Ta flamme vacille : agis avant <strong>{quand(c.driftAt, now)}</strong>
            </span>
          ) : (
            <span>Ta flamme s'éteint : agis maintenant pour ne pas dériver.</span>
          )}
        </div>
      )}

      <div class="status-bar-jauges">
        <div class="status-bar-jauge" title={`Points de vie : ${c.hp} / ${c.hpMax}`}>
          <span class="status-bar-etiquette">PV</span>
          <div class="status-bar-piste">
            <div class="status-bar-remplissage status-bar-remplissage-pv" style={{ width: percent(hpRatio) }} />
          </div>
          <span class="status-bar-chiffre">
            {c.hp}/{c.hpMax}
          </span>
        </div>

        {c.manaMax > 0 && (
          <div class="status-bar-jauge" title={`Mana : ${c.mana} / ${c.manaMax}`}>
            <span class="status-bar-etiquette">Mana</span>
            <div class="status-bar-piste">
              <div class="status-bar-remplissage status-bar-remplissage-mana" style={{ width: percent(manaRatio) }} />
            </div>
            <span class="status-bar-chiffre">
              {c.mana}/{c.manaMax}
            </span>
          </div>
        )}
      </div>

      <div class="status-bar-ligne">
        <div class="status-bar-energie" role="img" aria-label={`Énergie : ${c.energy} sur ${c.energyMax}`}>
          {Array.from({ length: c.energyMax }, (_, i) => (
            <span
              key={i}
              class={i < c.energy ? "status-bar-eclair status-bar-eclair-plein" : "status-bar-eclair status-bar-eclair-vide"}
              aria-hidden="true"
            >
              ⚡
            </span>
          ))}
        </div>

        <div class={hungry ? "status-bar-faim status-bar-faim-affame" : "status-bar-faim"} title={`Satiété : ${c.satiety} / 100`}>
          <span aria-hidden="true">{hungry ? "🥀" : "🍞"}</span>
          <div class="status-bar-piste status-bar-piste-fine">
            <div class="status-bar-remplissage status-bar-remplissage-faim" style={{ width: percent(hungerRatio) }} />
          </div>
        </div>

        <div class="status-bar-flamme" role="img" aria-label={`Flamme : ${c.flame} sur 3`}>
          {[0, 1, 2].map((i) => (
            <span key={i} class={i < c.flame ? "status-bar-feu status-bar-feu-allume" : "status-bar-feu status-bar-feu-eteint"} aria-hidden="true">
              🔥
            </span>
          ))}
        </div>

        <div class="status-bar-or" title={`${c.gold} pièces d'or`}>
          <span aria-hidden="true">🪙</span>
          <span class="status-bar-chiffre">{c.gold}</span>
        </div>

        <div class="status-bar-niveau" title={maxedXp ? `${c.xp} xp` : `${c.xp} / ${c.xpForNext} xp`}>
          Niv. {c.level}
        </div>
      </div>

      {c.conditions.length > 0 && (
        <ul class="status-bar-conditions" aria-label="Conditions">
          {c.conditions.map((k, i) => (
            <li key={`${k.id}-${i}`} class={`status-bar-condition status-bar-condition-${k.id}`}>
              <span aria-hidden="true">{CONDITION_ICONS[k.id]}</span> {CONDITION_LABELS[k.id]}
            </li>
          ))}
        </ul>
      )}

      <div
        class="status-bar-xp"
        role="progressbar"
        aria-label="Expérience"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(xpRatio * 100)}
      >
        <div class="status-bar-xp-fill" style={{ width: percent(xpRatio) }} />
      </div>
    </section>
  );
}
